import * as React from 'react';
import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const AnimateSvgPath = ({ d, duration = 2, fillColor = "rgba(255, 255, 255, 0)", ...props }) => {
    const icon = {
        hidden: {
            pathLength: 0,
            fill: "rgba(255, 255, 255, 0)",
        },
        visible: {
            pathLength: 1,
            fill: fillColor,
        },
    };
    return (React.createElement(motion.path, { ...props, d: d, variants: icon, initial: "hidden", animate: "visible", transition: {
            default: { duration, ease: "easeInOut" },
            fill: { duration, ease: [1, 0, 0.8, 1] },
        } }));
};

const MotionBlockData = [
    {
        variant: "div",
        component: motion.div,
    },
    {
        variant: "p",
        component: motion.p,
    },
    {
        variant: "a",
        component: motion.a,
    },
    {
        variant: "ul",
        component: motion.ul,
    },
    {
        variant: "h1",
        component: motion.h1,
    },
    {
        variant: "h2",
        component: motion.h2,
    },
    {
        variant: "h3",
        component: motion.h3,
    },
];

const FadeAnimation = ({ children, variant, delay = 0, duration = 0.5, className, initial, animate, exit, exitAnimation = false, }) => {
    const motionComponent = MotionBlockData.find((item) => item.variant === variant)?.component;
    if (!motionComponent) {
        console.error(`Motion component for variant "${variant}" not found.`);
        return null;
    }
    const properties = {
        initial: initial,
        animate: animate,
        exit: exitAnimation ? exit : undefined,
        transition: { delay, duration },
        className,
    };
    return React.createElement(motionComponent, properties, children);
};
const FadeUp = (props) => (React.createElement(FadeAnimation, { ...props, initial: { y: 20, opacity: 0 }, animate: { y: 0, opacity: 1 }, exit: { y: -20, opacity: 0 } }));
const FadeDown = (props) => (React.createElement(FadeAnimation, { ...props, initial: { y: -20, opacity: 0 }, animate: { y: 0, opacity: 1 }, exit: { y: 20, opacity: 0 } }));
const FadeHorizontal = ({ children, variant, delay = 0, duration = 0.5, className, initial, animate, exit, exitAnimation, }) => {
    const motionComponent = MotionBlockData.find((item) => item.variant === variant)?.component;
    if (!motionComponent) {
        console.error(`Motion component for variant "${variant}" not found.`);
        return null;
    }
    const properties = {
        initial: initial,
        animate: animate,
        exit: exitAnimation ? exit : undefined,
        transition: { delay, duration },
        className,
    };
    return React.createElement(motionComponent, properties, children);
};
const FadeRight = (props) => (React.createElement(FadeHorizontal, { ...props, initial: { x: -20, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: 20, opacity: 0 } }));
const FadeLeft = (props) => (React.createElement(FadeHorizontal, { ...props, initial: { x: 20, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: -20, opacity: 0 } }));

function useParallax(value, distance) {
    return useTransform(value, [0, 1], [-distance, distance]);
}
function ParallaxImageAndText({ imageSrc, altText = "Parallax Image", children, }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref });
    const y = useParallax(scrollYProgress, 300);
    return (React.createElement("section", { className: "parallax-image-and-text-section", ref: ref },
        React.createElement("div", { className: "image" },
            React.createElement("img", { src: imageSrc, alt: altText })),
        React.createElement(motion.div, { className: "text-div", style: { y } }, children)));
}

export { AnimateSvgPath, FadeDown, FadeLeft, FadeRight, FadeUp, ParallaxImageAndText };
